import { isAxiosError } from 'axios'
import { adminApiAxios, apiAxios } from '@/api'
import { fetchWrapper } from '../../reducers/fetch'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { PracticeType } from '@/constants'
import type { RecommendedOffersFetchResult } from '@/features/compliance/plan-pricing/recommendation.types'

export enum PRODUCT_METADATA_KEYS {
  // To differentiate between primary (core) products vs non-primary (add-on) products
  heardProductType = 'heard-product-type',

  // To store product eligibility rules based on tax entity and practice type
  eligibility = 'eligibility',
  activeInSignUp = 'activeInSignUp',
  displayName = 'displayName',
}

export const PAYMENT_SUCCESS_SEARCH_PARAM = 'paymentSuccess'

export const STRIPE_CANCELLATION_REASON_KEYS = {
  missingFeatures: 'missing_features',
  customerService: 'customer_service',
  other: 'other',
  lowQuality: 'low_quality',
  switchedService: 'switched_service',
  tooComplex: 'too_complex',
  tooExpensive: 'too_expensive',
  unused: 'unused',
}

export type EligibilityRule = {
  taxEntityType: TAX_ENTITY_TYPES
  practiceType: PracticeType
}

export enum PlanTier {
  starter = 'starter',
  lite = 'lite',
  essential = 'essential',
  essentialPlus = 'essential_plus',
  premium = 'premium',
  premiumPlus = 'premium_plus',
  liteScorpGroup = 'lite_scorp_group',
}
export interface StripeProductMetadata {
  'heard-product-type'?: 'primary'
  /** Written only by the catalog provisioner, so its absence marks a legacy product. */
  'heard-product-key'?: string
  eligibility?: EligibilityRule[]
  activeInSignUp?: boolean
  displayName?: string
  tier?: PlanTier
}

export const STRIPE_PRICE_MONTH_INTERVAL = 'month'

export interface StripePrice {
  id: string
  object: 'price'
  active: boolean
  billing_scheme: string
  created: number
  currency: string
  livemode: boolean
  metadata: unknown
  nickname: string
  product: string
  recurring: {
    aggregate_usage: null
    interval: 'month' | 'year' | 'day' | 'week'
    interval_count: number
    trial_period_days: null
    usage_type: 'metered' | 'licensed'
  }
  tax_behavior: string
  tiers_mode: null
  transform_quantity: null
  type: 'one_time' | 'recurring'
  unit_amount: number
  unit_amount_decimal: string
}

export interface StripeProduct {
  id: string
  object: 'product'
  active: boolean
  attributes: unknown[]
  created: number
  default_price: StripePrice
  description: string
  features?: { name: string }[]
  images: string[]
  livemode: boolean
  metadata: StripeProductMetadata
  name: string
  statement_descriptor: string
  tax_code: string
  unit_label: string
  updated: number
  url: string
}

export interface StripeCoupon {
  id: string
  object: 'coupon'
  amount_off: number | null
  created: number
  currency: string | null
  duration: 'forever' | 'once' | 'repeating'
  duration_in_months: number | null
  metadata: Record<string, string> | null
  name: string | null
  percent_off: number | null
  applies_to?: {
    products: string[]
  } | null
  currency_options?: Record<string, { amount_off: number }> | null
  livemode: boolean
  max_redemptions: number | null
  redeem_by: number | null
  times_redeemed: number
  valid: boolean
}

export type StripeLineItem = {
  priceId: string
  quantity?: number
}

export const FETCH_CHECKOUT_SESSION_KEY = 'FETCH_CHECKOUT_SESSION_KEY'
export const fetchCheckoutSession = (data: {
  userId: number
  lineItems: StripeLineItem[]
  success_url: {
    url: string
    external?: boolean
  }
  cancel_url: {
    url: string
    external?: boolean
  }
  freeTrialEnabled?: boolean
  freeTrialSearchParamValue?: string | null
  // Optionally pass in a coupon ID to apply to the subscription
  couponId?: string | null
  // defaults to subscription on backend
  mode?: 'payment' | 'subscription'
  metadata?: Record<string, string>
  custom_text?: {
    [key: string]: {
      message: string
    }
  }
}) =>
  fetchWrapper({
    fetchKey: FETCH_CHECKOUT_SESSION_KEY,
    defaultErrorMessage: 'Unable to fetch checkout session.',
    defaultValue: null,
    fetchFunction: () =>
      apiAxios
        // There is a LOT more data that comes back with this but we only use the url
        .post<{ url: string }>('/finances/api/v1/stripe/create_session', data)
        .then((json) => json.data),
  })

/*
  This method is used to fetch the billing portal session, which allows us
  to redirect the user to the Stripe billing / plan management portal
*/
export const fetchBillingPortalSession = (isUpsell?: boolean) =>
  fetchWrapper({
    defaultErrorMessage: 'Unable to fetch billing portal session session.',
    fetchFunction: () =>
      apiAxios
        // There is a LOT more data that comes back with this but we only use the url
        .post<{ url: string }>(
          '/finances/api/v1/stripe/billing_portal_session',
          { isUpsell }
        )
        .then((json) => json.data),
  })

export const FETCH_STRIPE_PRODUCTS_KEY = 'FETCH_STRIPE_PRODUCTS_KEY'
export const fetchEligibleStripeProducts = () =>
  fetchWrapper({
    fetchKey: FETCH_STRIPE_PRODUCTS_KEY,
    fetchFunction: async () => {
      const json = await apiAxios.get<StripeProduct[]>(
        '/finances/api/v1/stripe/products/eligible'
      )
      return json.data
    },
  })

export const FETCH_RECOMMENDED_OFFERS_KEY = 'FETCH_RECOMMENDED_OFFERS_KEY'
export const fetchRecommendedOffers = ({
  context,
  recommendedTier,
}: {
  context: 'signup' | 'report' | 'plan_change_page'
  recommendedTier?: string
}) =>
  fetchWrapper({
    fetchKey: FETCH_RECOMMENDED_OFFERS_KEY,
    fetchFunction: async () => {
      try {
        const json = await apiAxios.get<RecommendedOffersFetchResult>(
          '/finances/api/v2/plan_recommendation/offers',
          { params: { context, recommendedTier } }
        )
        return json.data
      } catch (error) {
        // A 404 means the route's feature flag is off, not a missing endpoint. Handle
        // it here so fetchWrapper never sees a rejection and never raises a global
        // error for what is a deliberate, expected state.
        if (isAxiosError(error) && error.response?.status === 404) {
          const disabled: RecommendedOffersFetchResult = {
            status: 'feature_disabled',
          }
          return disabled
        }
        throw error
      }
    },
  })

/*
  This method is used to reactive a User's Plan _before_ the end date has passed
*/
export const REACTIVATE_USER_SUBSCRIPTION_KEY =
  'REACTIVATE_USER_SUBSCRIPTION_KEY'
export const reactivateUserSubscription = () =>
  fetchWrapper({
    fetchKey: REACTIVATE_USER_SUBSCRIPTION_KEY,
    defaultErrorMessage:
      'Unable to reactivate User subscription. Please contact support.',
    fetchFunction: () =>
      apiAxios
        .post('/finances/api/v1/stripe/reactivate_subscription')
        .then((json) => json.data),
  })

/*
  Submit cancellation
*/
export const SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY =
  'SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY'
export const submitCancelStripeSubscriptionAtPeriodEnd = (cancellationDetails: {
  cancellationComment?: string
  cancellationReason?: string
}) =>
  fetchWrapper({
    fetchKey: SUMBIT_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END_KEY,
    defaultErrorMessage:
      'Unable to cancel User subscription. Please try again.',
    fetchFunction: () =>
      apiAxios
        .post('/finances/api/v1/stripe/cancel_subscription', {
          cancellationDetails,
        })
        .then((json) => json.data),
  })

/*
  This method is used to submit a cancellation request if users cannot self-serve
*/
export const submitCancellationRequest = (userId?: number) =>
  fetchWrapper({
    defaultErrorMessage: 'Unable to submit cancellation request',
    fetchFunction: async () => {
      const json = await apiAxios.post(
        `/finances/api/v1/users/${userId}/submit_cancellation_request`
      )
      return json.data
    },
  })

/**
 * This route is used for users affected by the 7/28/2025 incident.
 * It can be removed after 7/28/2026.
 */
export const fetchNextBillingDate = ({ enabled }: { enabled: boolean }) =>
  fetchWrapper({
    fetchFunction: async () => {
      if (!enabled) {
        return null
      }
      const json = await apiAxios.get<{ nextInvoiceDate: string | null }>(
        '/finances/api/v2/temporary/next_invoice_date'
      )
      return json.data.nextInvoiceDate
    },
  })

export const fetchAllStripeProducts = () =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await adminApiAxios.get<StripeProduct[]>(
        '/finances/api/v1/admin/stripe/products'
      )
      return json.data
    },
  })
